import { ObjectStatusBadgeProps, ObjectStatusStyle, ObjectStatusStyles } from "./ObjectStatusBadge.types"

// Shared looks, named for the state they signal across objects
const draft: ObjectStatusStyle = { colorScheme: "gray", icon: "circle-dashed" }
const unsent: ObjectStatusStyle = { colorScheme: "violet", icon: "circle-dashed" }
const inProgress: ObjectStatusStyle = { colorScheme: "blue", icon: "circle-half-stroke", iconRotation: 180 }
const overdue: ObjectStatusStyle = { colorScheme: "tomato", icon: "circle-exclamation" }
const completed: ObjectStatusStyle = { colorScheme: "orange", icon: "circle-check" }
const finalized: ObjectStatusStyle = { colorScheme: "jade", icon: "circle-check" }
const cancelled: ObjectStatusStyle = { colorScheme: "gray", icon: "circle-xmark" }
const active: ObjectStatusStyle = { colorScheme: "jade", icon: "circle-minus", iconRotation: 90 }
const inactive: ObjectStatusStyle = { colorScheme: "gray", icon: "ban" }
const onHold: ObjectStatusStyle = { colorScheme: "crimson", icon: "circle-stop" }

// Companies with on-hold sub-status groups colour internal holds differently; external holds look like a plain hold
const jobOnHoldStyles: Record<NonNullable<Job["status_color_override"]>, ObjectStatusStyle> = {
    "jobs-on-hold-external": onHold,
    "jobs-on-hold-internal": { colorScheme: "brown", icon: "circle-stop" },
}

export const objectStatusStyles: ObjectStatusStyles = {
    estimate: {
        Draft: draft,
        Unsent: unsent,
        "Awaiting Approval": inProgress,
        Expired: overdue,
        Unconverted: completed,
        Jobbed: finalized,
        Invoiced: finalized,
        Lost: cancelled,
        Cancelled: cancelled,
    },
    job: {
        Draft: draft,
        Unscheduled: unsent,
        Upcoming: inProgress,
        "Past Due": overdue,
        Active: { colorScheme: "jade", icon: "circle-play" },
        "Quick-Paused": { colorScheme: "amber", icon: "circle-pause" },
        "On Hold": onHold,
        Uninvoiced: completed,
        Unestimated: completed,
        Finalized: finalized,
        Cancelled: cancelled,
    },
    job_series: {
        Open: { colorScheme: "jade", icon: "circle-small" },
        Closed: { colorScheme: "gray", icon: "circle-small" },
    },
    invoice: {
        Draft: draft,
        Unsent: unsent,
        Outstanding: inProgress,
        Overdue: overdue,
        Paid: finalized,
        Voided: cancelled,
        Forgiven: cancelled,
    },
    credit_note: {
        Draft: draft,
        Unsent: unsent,
        Issued: finalized,
        Voided: cancelled,
    },
    purchase_order: {
        Draft: draft,
        Unsent: unsent,
        Sent: { colorScheme: "blue", icon: "circle-quarter-stroke", iconRotation: 90 },
        Acknowledged: { colorScheme: "jade", icon: "circle-quarter-stroke", iconRotation: 90 },
        "In Transit": { colorScheme: "cyan", icon: "circle-half-stroke", iconRotation: 180 },
        Unstocked: { colorScheme: "amber", icon: "circle-three-quarters-stroke", iconRotation: 270 },
        Unpaid: { colorScheme: "orange", icon: "circle-three-quarters-stroke", iconRotation: 270 },
        Paid: finalized,
        Cancelled: cancelled,
    },
    bill: {
        Draft: draft,
        Unsent: unsent,
        Outstanding: inProgress,
        Overdue: overdue,
        Paid: finalized,
        Voided: cancelled,
    },
    pricebook_item: {
        Confirmed: active,
        "Needs Review": { colorScheme: "amber", icon: "clock" },
    },
    client: {
        Active: active,
        Inactive: inactive,
    },
    vendor: {
        Active: active,
        Inactive: inactive,
    },
}

export function getObjectStatusStyle(props: ObjectStatusBadgeProps): ObjectStatusStyle {
    const { objectType, status } = props

    if (objectType === "job" && props.statusColorOverride) {
        return jobOnHoldStyles[props.statusColorOverride]
    } else {
        // Each record is complete for its object's statuses; TS just can't correlate the two unions
        return (objectStatusStyles[objectType] as Record<string, ObjectStatusStyle>)[status]
    }
}
